// Packages
import React, { useCallback, useContext, useEffect, useState } from 'react';

import { StyledButton } from './SHDButton.styles';

// Helpers and utilities
import { getComponentClassName } from '@theme/utils/data';
import { ResponsiveContext } from 'grommet';

import { analytics } from '@services/analytics';

// Types
import type { SHDButtonProps } from './SHDButton.d';

const SHDButton: React.FC<SHDButtonProps> = ({
  className = 'button',
  onClick,
  onError,
  primary = false,
  secondary = false,
  tertiary = false,
  warning = false,
  plain = false,
  dark = false,
  pill = false,
  border = false,
  ...props
}) => {
  // Constants
  const componentClassName = 'button';
  const { trackingContext, trackingEvent, trackingProperties } = props;

  // States
  const [utilityClassNames, setUtilityClassNames] = useState<string[]>();

  // Hooks and contexts
  const viewportSize = useContext(ResponsiveContext);

  // Handlers
  const handleButtonTracking = () => {
    if (!trackingEvent) {
      return;
    }

    if (trackingEvent && !trackingContext) {
      onError &&
        onError({
          message:
            'Tracking context is required when tracking event is provided',
        });
    }

    analytics.track(trackingEvent, {
      context: trackingContext,
      ...trackingProperties,
    });
  };

  const handleUtilityClassNameUpdates = useCallback(() => {
    const utilityClassArr: string[] = [];

    if (className) {
      className.split(' ').forEach((name) => {
        utilityClassArr.push(name);
      });
    }

    // Viewport size
    utilityClassArr.push(`is-${viewportSize}`);
    setUtilityClassNames(utilityClassArr);

    // Boolean props
    primary && utilityClassArr.push('is-primary');
    secondary && utilityClassArr.push('is-secondary');
    tertiary && utilityClassArr.push('is-tertiary');
    plain && utilityClassArr.push('is-plain');
    dark && utilityClassArr.push('is-dark');
    pill && utilityClassArr.push('is-pill');
    border && utilityClassArr.push('is-border');
    warning && utilityClassArr.push('is-warning');

    // Non-boolean props
    if (props.icon) {
      utilityClassArr.push('has-icon');
    }
  }, [
    className,
    viewportSize,
    primary,
    secondary,
    tertiary,
    plain,
    dark,
    pill,
    border,
    props.icon,
    warning,
  ]);

  // Effects
  useEffect(() => {
    handleUtilityClassNameUpdates();
  }, [handleUtilityClassNameUpdates]);

  return (
    <StyledButton
      {...props}
      aria-label="button"
      className={getComponentClassName(componentClassName, utilityClassNames)}
      fill="horizontal"
      role="button"
      onClick={() => {
        onClick();
        handleButtonTracking();
      }}
    />
  );
};

export default SHDButton;
